/**
 * hoshinService.ts
 * Hoshin Kanri — Lean Strategic Deployment service.
 *
 * Árvore de cascateamento: Director (Objective) → Manager (Tactical Goal) → Coordinator/Operator (Action)
 *
 * Rules:
 *  - Always filter by organization_id (tenant isolation)
 *  - Never call Supabase directly in components
 *  - Mutations enforce role-type poka-yoke on the app layer;
 *    DB-level enforcement is handled by RLS policies
 */

import { supabase } from '../lib/supabaseClient';
import type { RoleType } from './organizationService';

// ============================================================================
// Types
// ============================================================================

export type ObjectiveStatus = 'active' | 'achieved' | 'canceled';
export type TacticalGoalStatus = 'on_track' | 'at_risk' | 'delayed' | 'completed';

/**
 * computeGoalStatus
 * Deriva o status da meta tática em tempo real (Lean — sem entrada manual):
 *  - progresso 100%                         → 'completed'
 *  - hoje > data-alvo e progresso < 100%    → 'delayed'
 *  - progresso abaixo da linha de tendência → 'at_risk'
 *  - caso contrário                         → 'on_track'
 */
export function computeGoalStatus(
  progress: number,
  createdAt: string,
  targetDate: string | null,
): TacticalGoalStatus {
  if (progress >= 100) return 'completed';
  if (!targetDate) return 'on_track';

  const target = new Date(targetDate);
  if (isNaN(target.getTime())) return 'on_track';

  const now = new Date();
  if (now > target) return 'delayed';

  const created = new Date(createdAt);
  const totalMs = target.getTime() - created.getTime();
  const elapsedMs = now.getTime() - created.getTime();
  const idealProgress = totalMs > 0
    ? Math.min(100, Math.max(0, (elapsedMs / totalMs) * 100))
    : 0;

  // Tolerância de 10 p.p. abaixo da linha ideal antes de sinalizar risco.
  if (progress < idealProgress - 10) return 'at_risk';
  return 'on_track';
}

export interface HoshinObjective {
  id: string;
  organization_id: string;
  created_by: string;
  title: string;
  description: string | null;
  target_date: string;
  status: ObjectiveStatus;
  created_at: string;
  updated_at: string;
}

export interface HoshinTacticalGoal {
  id: string;
  objective_id: string;
  department_id: string;
  assigned_to: string;
  title: string;
  /** Progresso REAL (%) — derivado da média de conclusão das ações vinculadas. */
  progress: number;
  /** (Legado) meta planejada numérica em %. */
  target_value: number;
  /** Meta numérica / indicador alvo em texto livre (ex: "OEE 85%"). */
  target_indicator: string | null;
  /** Data-alvo (horizonte) da meta — usada para derivar o status. */
  target_date: string | null;
  status: TacticalGoalStatus;
  created_at: string;
  updated_at: string;
}

/** Enriched tactical goal for the strategic tree — includes action aggregates */
export interface TacticalGoalNode extends HoshinTacticalGoal {
  action_count: number;
  completed_action_count: number;
}

/** Full cascading tree node returned by getStrategicTree() */
export interface ObjectiveNode extends HoshinObjective {
  goals: TacticalGoalNode[];
}

export interface CreateObjectiveParams {
  organization_id: string;
  created_by: string;
  title: string;
  description?: string;
  target_date: string; // ISO 8601
}

export interface CreateTacticalGoalParams {
  objective_id: string;
  department_id: string;
  assigned_to: string;
  title: string;
  /** Meta numérica / indicador alvo (texto livre). Opcional. */
  target_indicator?: string | null;
  /** Data-alvo (ISO) da meta. Opcional. */
  target_date?: string | null;
}

/** Campos de PLANEJAMENTO editáveis da meta tática (progress/status são derivados). */
export interface UpdateTacticalGoalParams {
  title?: string;
  assigned_to?: string;
  department_id?: string;
  target_date?: string | null;
  target_indicator?: string | null;
}

/** Ação da Central de Ações disponível para vínculo com uma Meta Tática. */
export interface LinkableAction {
  id: string;
  title: string;
  status: string;
  hoshin_goal_id: string | null;
}

// ============================================================================
// Role guard (app-layer poka-yoke)
// ============================================================================

const DIRECTOR_ROLES: RoleType[] = ['director'];
const MANAGER_ROLES: RoleType[] = ['manager', 'director'];

function assertRole(userRoleType: RoleType, allowed: RoleType[], action: string): void {
  if (!allowed.includes(userRoleType)) {
    throw new Error(
      `Permissão negada: a ação "${action}" requer papel ${allowed.join(' ou ')}. Papel atual: ${userRoleType}.`,
    );
  }
}

// ============================================================================
// Queries
// ============================================================================

/**
 * getStrategicTree
 *
 * Retorna a árvore completa de desdobramento estratégico da organização.
 * Um único round-trip: busca objectives + goals + contagem de ações em paralelo,
 * depois monta a árvore no cliente para evitar JOINs profundos que podem
 * ser limitados pelo PostgREST.
 */
export async function getStrategicTree(organizationId: string): Promise<ObjectiveNode[]> {
  const [objectivesRes, goalsRes, actionsRes] = await Promise.all([
    supabase
      .from('hoshin_objectives')
      .select('id, organization_id, created_by, title, description, target_date, status, created_at, updated_at')
      .eq('organization_id', organizationId)
      .order('target_date', { ascending: true }),

    supabase
      .from('hoshin_tactical_goals')
      .select(
        'id, objective_id, department_id, assigned_to, title, progress, target_value, target_indicator, target_date, status, created_at, updated_at',
      )
      .in(
        'objective_id',
        // sub-query emulada: carrega IDs via tabela já filtrada por org na primeira query
        // Se a primeira query ainda não resolveu, usamos um array vazio temporariamente;
        // na prática este Promise.all resolve tudo antes de montar a árvore abaixo.
        [], // preenchido após resolução — ver lógica abaixo
      ),

    supabase
      .from('actions')
      .select('id, hoshin_goal_id, status')
      .eq('organization_id', organizationId)
      .not('hoshin_goal_id', 'is', null),
  ]);

  if (objectivesRes.error) throw objectivesRes.error;

  const objectives: HoshinObjective[] = objectivesRes.data ?? [];
  const objectiveIds = objectives.map(o => o.id);

  // Re-fetch goals scoped to resolved objective IDs (avoids subquery limitation above)
  let goals: HoshinTacticalGoal[] = [];
  if (objectiveIds.length > 0) {
    const { data, error } = await supabase
      .from('hoshin_tactical_goals')
      .select(
        'id, objective_id, department_id, assigned_to, title, progress, target_value, target_indicator, target_date, status, created_at, updated_at',
      )
      .in('objective_id', objectiveIds)
      .order('status', { ascending: true });

    if (error) {
      // Fallback: ambiente onde as migrações (target_value / target_date) ainda não rodaram.
      const missingColumn =
        error.code === '42703' ||
        error.code === 'PGRST204' ||
        /target_value|target_indicator|target_date|column|does not exist|schema cache/i.test(error.message);
      if (!missingColumn) throw error;

      const { data: legacyData, error: legacyError } = await supabase
        .from('hoshin_tactical_goals')
        .select(
          'id, objective_id, department_id, assigned_to, title, progress, status, created_at, updated_at',
        )
        .in('objective_id', objectiveIds)
        .order('status', { ascending: true });

      if (legacyError) throw legacyError;
      goals = (legacyData ?? []).map(g => ({ ...g, target_value: 0, target_indicator: null, target_date: null })) as HoshinTacticalGoal[];
    } else {
      goals = data ?? [];
    }
  }

  // Mapa objetivo → data-alvo (fallback de horizonte para metas sem data própria)
  const objectiveTargetDate = new Map(objectives.map(o => [o.id, o.target_date]));

  // Use action data from the first parallel fetch (already scoped by org + hoshin_goal_id IS NOT NULL)
  if (actionsRes.error) throw actionsRes.error;
  const actions: { id: string; hoshin_goal_id: string | null; status: string }[] =
    actionsRes.data ?? [];

  // Build action aggregates per goal
  const actionCountByGoal = new Map<string, number>();
  const completedCountByGoal = new Map<string, number>();

  for (const action of actions) {
    if (!action.hoshin_goal_id) continue;
    actionCountByGoal.set(
      action.hoshin_goal_id,
      (actionCountByGoal.get(action.hoshin_goal_id) ?? 0) + 1,
    );
    if (action.status === 'completed') {
      completedCountByGoal.set(
        action.hoshin_goal_id,
        (completedCountByGoal.get(action.hoshin_goal_id) ?? 0) + 1,
      );
    }
  }

  // Build goal nodes
  const goalNodesByObjective = new Map<string, TacticalGoalNode[]>();
  for (const goal of goals) {
    const actionCount = actionCountByGoal.get(goal.id) ?? 0;
    const completedCount = completedCountByGoal.get(goal.id) ?? 0;
    // Progresso REAL: média de conclusão das ações operacionais vinculadas.
    // Sem ações vinculadas → 0% (ainda não há execução mensurável).
    const computedProgress = actionCount > 0
      ? Math.round((completedCount / actionCount) * 100)
      : 0;
    // Status derivado automaticamente (data-alvo da meta, ou a do objetivo como horizonte).
    const deadline = goal.target_date ?? objectiveTargetDate.get(goal.objective_id) ?? null;
    const computedStatus = computeGoalStatus(computedProgress, goal.created_at, deadline);
    const node: TacticalGoalNode = {
      ...goal,
      progress: computedProgress,
      status: computedStatus,
      action_count: actionCount,
      completed_action_count: completedCount,
    };
    if (!goalNodesByObjective.has(goal.objective_id)) {
      goalNodesByObjective.set(goal.objective_id, []);
    }
    goalNodesByObjective.get(goal.objective_id)!.push(node);
  }

  // Assemble final tree
  return objectives.map<ObjectiveNode>(obj => ({
    ...obj,
    goals: goalNodesByObjective.get(obj.id) ?? [],
  }));
}

// ============================================================================
// Mutations
// ============================================================================

/**
 * createStrategicObjective
 * Role guard: apenas 'director'.
 */
export async function createStrategicObjective(
  params: CreateObjectiveParams,
  userRoleType: RoleType,
): Promise<HoshinObjective> {
  assertRole(userRoleType, DIRECTOR_ROLES, 'criar Objetivo Estratégico');

  const { data, error } = await supabase
    .from('hoshin_objectives')
    .insert({
      organization_id: params.organization_id,
      created_by: params.created_by,
      title: params.title.trim(),
      description: params.description?.trim() ?? null,
      target_date: params.target_date,
    })
    .select(
      'id, organization_id, created_by, title, description, target_date, status, created_at, updated_at',
    )
    .single();

  if (error) throw error;
  return data as HoshinObjective;
}

/**
 * createTacticalGoal
 * Role guard: 'manager' ou 'director'.
 * O objective_id referenciado é validado indiretamente pela RLS (pertence à mesma org).
 */
export async function createTacticalGoal(
  params: CreateTacticalGoalParams,
  userRoleType: RoleType,
): Promise<HoshinTacticalGoal> {
  assertRole(userRoleType, MANAGER_ROLES, 'criar Meta Tática');

  // progress (real) inicia em 0 — será derivado automaticamente da conclusão das ações.
  const { data, error } = await supabase
    .from('hoshin_tactical_goals')
    .insert({
      objective_id: params.objective_id,
      department_id: params.department_id,
      assigned_to: params.assigned_to,
      title: params.title.trim(),
      progress: 0,
      target_indicator: params.target_indicator?.trim() || null,
      target_date: params.target_date ?? null,
      status: 'on_track',
    })
    .select(
      'id, objective_id, department_id, assigned_to, title, progress, target_value, target_indicator, target_date, status, created_at, updated_at',
    )
    .single();

  if (error) throw error;
  return data as HoshinTacticalGoal;
}

/**
 * updateTacticalGoal
 * Atualiza apenas campos de PLANEJAMENTO (título, responsável, departamento, data-alvo, meta).
 * progress e status NÃO são editáveis — são derivados automaticamente.
 * Role guard: 'manager' ou 'director'.
 */
export async function updateTacticalGoal(
  goalId: string,
  params: UpdateTacticalGoalParams,
  userRoleType: RoleType,
): Promise<void> {
  assertRole(userRoleType, MANAGER_ROLES, 'editar Meta Tática');

  const payload: Record<string, unknown> = {};
  if (typeof params.title === 'string') payload.title = params.title.trim();
  if (typeof params.assigned_to === 'string') payload.assigned_to = params.assigned_to;
  if (typeof params.department_id === 'string') payload.department_id = params.department_id;
  if (params.target_date !== undefined) payload.target_date = params.target_date;
  if (params.target_indicator !== undefined) {
    payload.target_indicator = params.target_indicator?.trim() || null;
  }

  if (Object.keys(payload).length === 0) return;

  const { error } = await supabase
    .from('hoshin_tactical_goals')
    .update(payload)
    .eq('id', goalId);

  if (error) throw error;
}

/**
 * linkActionToHoshin
 * Vincula um Plano de Ação existente a uma Meta Tática Hoshin.
 * Permite null para desvincular.
 */
export async function linkActionToHoshin(
  actionId: string,
  hoshinGoalId: string | null,
): Promise<void> {
  const { error } = await supabase
    .from('actions')
    .update({ hoshin_goal_id: hoshinGoalId, updated_at: new Date().toISOString() })
    .eq('id', actionId);

  if (error) throw error;
}

/**
 * updateObjectiveStatus
 * Role guard: 'director'.
 */
export async function updateObjectiveStatus(
  objectiveId: string,
  status: ObjectiveStatus,
  userRoleType: RoleType,
): Promise<void> {
  assertRole(userRoleType, DIRECTOR_ROLES, 'atualizar status de Objetivo Estratégico');

  const { error } = await supabase
    .from('hoshin_objectives')
    .update({ status })
    .eq('id', objectiveId);

  if (error) throw error;
}

/**
 * getGoalActions
 * Retorna as ações vinculadas a uma Meta Tática específica.
 * Usado para exibição compacta no painel de cascateamento.
 */
export async function getGoalActions(
  goalId: string,
): Promise<{ id: string; title: string; status: string }[]> {
  const { data, error } = await supabase
    .from('actions')
    .select('id, title, status')
    .eq('hoshin_goal_id', goalId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data ?? []) as { id: string; title: string; status: string }[];
}

/**
 * getLinkableActions
 * Lista as ações da Central de Ações da organização para vínculo a Metas Táticas.
 * Retorna também o hoshin_goal_id atual, permitindo distinguir ações livres das já vinculadas.
 */
export async function getLinkableActions(organizationId: string): Promise<LinkableAction[]> {
  const { data, error } = await supabase
    .from('actions')
    .select('id, title, status, hoshin_goal_id')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data ?? []) as LinkableAction[];
}

export async function getLinkedExecutionActions(organizationId: string): Promise<{
  id: string;
  title: string;
  status: string;
  project_id: string | null;
  due_date: string | null;
  assigned_to: string | null;
  hoshin_goal_id: string | null;
  created_at: string;
  updated_at: string;
}[]> {
  const { data, error } = await supabase
    .from('actions')
    .select('id, title, status, project_id, due_date, assigned_to, hoshin_goal_id, created_at, updated_at')
    .eq('organization_id', organizationId)
    .not('hoshin_goal_id', 'is', null)
    .order('updated_at', { ascending: false });

  if (error) throw error;
  return (data ?? []) as {
    id: string;
    title: string;
    status: string;
    project_id: string | null;
    due_date: string | null;
    assigned_to: string | null;
    hoshin_goal_id: string | null;
    created_at: string;
    updated_at: string;
  }[];
}

/**
 * Role guard: 'manager' ou 'director'.
 */
export async function updateGoalProgress(
  goalId: string,
  progress: number,
  status: TacticalGoalStatus,
  userRoleType: RoleType,
): Promise<void> {
  assertRole(userRoleType, MANAGER_ROLES, 'atualizar progresso de Meta Tática');

  if (progress < 0 || progress > 100) {
    throw new Error('Progresso deve estar entre 0 e 100.');
  }

  const { error } = await supabase
    .from('hoshin_tactical_goals')
    .update({ progress, status })
    .eq('id', goalId);

  if (error) throw error;
}
